// ============================================================================
// Shared rich-content rendering for AI output.
//
// The DeepSeek endpoints return prose as MARKDOWN in their "*_html" fields.
// Rendering that string directly shows raw `#`, `**` and `|` characters, so we
// convert it to safe HTML here and style it with the `.rich-content` class in
// Modules.css. This is a dependency-free, good-enough Markdown subset
// (headings, lists, tables, quotes, code, bold/italic, links) — ported from the
// battle-tested converter that lived in LectureModule.
//
// Math spans ($inline$, $$display$$, also \[ \] and \( \)) are rendered to
// real typeset HTML with KaTeX right here — previously they were only wrapped
// in a styled span showing the raw LaTeX source.
// ============================================================================

import katex from 'katex';
import 'katex/dist/katex.min.css';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// One regex for every math delimiter the model emits.
const MATH_RE = /\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$[^$\n]+\$/g;

/** KaTeX needs real characters — model-returned HTML often carries escaped
 *  entities ("&lt;" for "<") inside math. Decode &amp; last so double-escaped
 *  sequences only unwrap once. */
function decodeMathEntities(tex: string): string {
  return tex
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/** Split a matched math span into its TeX source + display/inline mode. */
function parseMathMatch(m: string): { tex: string; display: boolean } {
  if (m.startsWith('$$') || m.startsWith('\\[')) {
    return { tex: decodeMathEntities(m.slice(2, -2).trim()), display: true };
  }
  if (m.startsWith('\\(')) {
    return { tex: decodeMathEntities(m.slice(2, -2).trim()), display: false };
  }
  return { tex: decodeMathEntities(m.slice(1, -1).trim()), display: false };
}

/** Heuristic: is a $...$ span real math rather than a dollar amount? */
function looksLikeMath(tex: string): boolean {
  return /\\[a-zA-Z]+|[_{}^]/.test(tex);
}

/** Render one math span to KaTeX HTML; on error show the raw source styled. */
function mathToHtml(raw: string): string {
  const { tex, display } = parseMathMatch(raw);
  // Dollar amounts ("$5 and $10") are not math — keep them as plain text.
  if (!display && !looksLikeMath(tex)) {
    return `<span class="math-inline">${escapeHtml(raw)}</span>`;
  }
  try {
    const html = katex.renderToString(tex, { displayMode: display, throwOnError: true });
    return display ? `<span class="math-block">${html}</span>` : html;
  } catch {
    return `<span class="math-inline">${escapeHtml(raw)}</span>`;
  }
}

/** Inline formatting: bold, italic, inline code, links, while protecting math. */
function formatInline(text: string): string {
  // Protect math ($...$, $$...$$, \[...\], \(...\)) so its contents aren't mangled.
  const math: string[] = [];
  let out = text.replace(MATH_RE, (m) => {
    math.push(m);
    return ` MATH${math.length - 1} `;
  });

  out = escapeHtml(out);
  out = out
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Restore math as typeset KaTeX HTML.
  out = out.replace(/ MATH(\d+) /g, (_m, i) => mathToHtml(math[Number(i)]));
  return out;
}

/** Strip markdown code fences and common AI preamble lines. */
export function cleanGeneratedHtml(raw: string): string {
  if (!raw) return '';
  let cleaned = raw.trim();
  cleaned = cleaned.replace(/^```(?:html|markdown|md)?\s*/i, '').replace(/```\s*$/i, '');
  cleaned = cleaned
    .replace(/^(?:Here'?s[\s\S]*?<br\/?>\s*)/i, '')
    .replace(/^(?:Below\s+is[\s\S]*?<br\/?>\s*)/i, '');
  cleaned = cleaned.replace(/```+/g, '');
  return cleaned.trim();
}

export function isLikelyHtml(text: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

function isTableSeparator(line: string): boolean {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?$/.test(line.trim());
}

function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

/** Convert a Markdown subset to an HTML string (no wrapper). */
export function convertMarkdownToHtml(markdown: string): string {
  // Collapse multi-line display-math blocks ($$...$$ / \[...\]) onto one line
  // so the per-line inline pass can see them — KaTeX ignores the newlines.
  const normalized = markdown
    .replace(/\$\$([\s\S]*?)\$\$/g, (_m, inner) => `$$${inner.replace(/\s*\n\s*/g, ' ').trim()}$$`)
    .replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `\\[${inner.replace(/\s*\n\s*/g, ' ').trim()}\\]`);
  const lines = normalized.replace(/\r\n/g, '\n').split('\n');
  let html = '';
  let listType: 'ul' | 'ol' | null = null;
  let inParagraph = false;
  let inCode = false;
  let codeBuf: string[] = [];

  const closeList = () => {
    if (listType) {
      html += listType === 'ul' ? '</ul>' : '</ol>';
      listType = null;
    }
  };
  const closeParagraph = () => {
    if (inParagraph) {
      html += '</p>';
      inParagraph = false;
    }
  };
  const flushCode = () => {
    if (codeBuf.length) {
      closeList();
      closeParagraph();
      html += `<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`;
      codeBuf = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Fenced code block
    if (/^```/.test(trimmed)) {
      if (inCode) {
        inCode = false;
        flushCode();
      } else {
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    if (!trimmed) {
      closeList();
      closeParagraph();
      continue;
    }

    // GFM table: header row, separator row, then body rows.
    if (trimmed.startsWith('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      closeList();
      closeParagraph();
      const headers = splitTableRow(trimmed);
      i += 2; // skip header + separator
      const body: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        body.push(splitTableRow(lines[i]));
        i++;
      }
      i--; // back up; outer loop increments
      html += '<table><thead><tr>';
      headers.forEach((h) => (html += `<th>${formatInline(h)}</th>`));
      html += '</tr></thead><tbody>';
      body.forEach((row) => {
        html += '<tr>';
        headers.forEach((_, c) => (html += `<td>${formatInline(row[c] || '')}</td>`));
        html += '</tr>';
      });
      html += '</tbody></table>';
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      closeList();
      closeParagraph();
      const level = headingMatch[1].length;
      html += `<h${level}>${formatInline(headingMatch[2])}</h${level}>`;
      continue;
    }

    if (/^>\s+/.test(trimmed)) {
      closeList();
      closeParagraph();
      html += `<blockquote>${formatInline(trimmed.replace(/^>\s+/, ''))}</blockquote>`;
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      closeParagraph();
      if (listType !== 'ol') {
        closeList();
        html += '<ol>';
        listType = 'ol';
      }
      html += `<li>${formatInline(trimmed.replace(/^\d+\.\s+/, ''))}</li>`;
      continue;
    }

    if (/^[-*+]\s+/.test(trimmed)) {
      closeParagraph();
      if (listType !== 'ul') {
        closeList();
        html += '<ul>';
        listType = 'ul';
      }
      html += `<li>${formatInline(trimmed.replace(/^[-*+]\s+/, ''))}</li>`;
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      closeList();
      closeParagraph();
      html += '<hr />';
      continue;
    }

    if (!inParagraph) {
      html += '<p>';
      inParagraph = true;
    } else {
      html += ' ';
    }
    html += formatInline(trimmed);
  }

  if (inCode) flushCode();
  closeList();
  closeParagraph();
  return html;
}

/**
 * Convert raw model output (Markdown or HTML) to displayable HTML.
 * Returns the INNER html — render inside `<div class="rich-content">`.
 */
export function renderRichContent(raw: string): string {
  const cleaned = cleanGeneratedHtml(raw);
  if (!cleaned) return '';
  if (isLikelyHtml(cleaned)) {
    // Already HTML (model sometimes returns markup) — normalise fences, then
    // still typeset any math spans inside the markup.
    return cleaned.replace(/\n{3,}/g, '\n\n').replace(MATH_RE, (m) => mathToHtml(m));
  }
  return convertMarkdownToHtml(cleaned);
}

/** Strip all HTML to plain text (for TXT export). */
export function richContentToPlainText(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // KaTeX output contains both MathML and visible spans — reading textContent
    // directly would duplicate every formula. Swap each .katex for its TeX source.
    tmp.querySelectorAll('.katex').forEach((el) => {
      const ann = el.querySelector('annotation[encoding="application/x-tex"]');
      el.replaceWith(document.createTextNode(ann?.textContent ? `$${ann.textContent}$` : el.textContent || ''));
    });
    tmp.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li,tr,br,hr,blockquote').forEach((el) => {
      el.append('\n');
      if (['H1', 'H2', 'H3', 'LI', 'TR'].includes(el.tagName)) el.append('\n');
    });
    return (tmp.textContent || tmp.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
  }
  return html.replace(/<[^>]+>/g, '').trim();
}
